import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaArrowRight, FaAngleRight, FaRegClock, FaRegUser } from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import { getRevealProps } from '../motion/scrollMotion';
import './ServiceDetailPage.css';
import './BlogPostPage.css';

const renderBlock = (block, i) => {
  if (block.type === 'h3') return <h3 className="post-h3" key={i}>{block.text}</h3>;
  if (block.type === 'list') {
    return (
      <ul className="post-list" key={i}>
        {block.items.map((item, idx) => <li key={idx}>{item}</li>)}
      </ul>
    );
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="post-quote" key={i}>
        <p>&ldquo;{block.text}&rdquo;</p>
        {block.attribution && <cite>— {block.attribution}</cite>}
      </blockquote>
    );
  }
  return <p className="post-p" key={i}>{block.text}</p>;
};

const ArticleDetailPage = ({ posts, basePath, listPath, listLabel, getBySlug }) => {
  const { slug } = useParams();
  const post = getBySlug(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) {
    return <Navigate to={listPath} replace />;
  }

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="svc-page">
      <PageNavbar />

      <section className="svc-hero post-hero">
        <div className="svc-hero-glow" />
        <div className="svc-container">
          <motion.div {...getRevealProps('fadeUp')}>
            <nav className="svc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaAngleRight />
              <Link to={listPath}>{listLabel}</Link>
              <FaAngleRight />
              <span>{post.title}</span>
            </nav>
            <span className="svc-eyebrow">{post.tag}{post.mitreId ? ` · ${post.mitreId}` : ''}</span>
            <h1 className="svc-hero-title post-title">{post.title}</h1>
            <div className="post-meta">
              <span><FaRegUser /> {post.author}</span>
              <span className="post-meta-dot">·</span>
              <span>{post.date}</span>
              <span className="post-meta-dot">·</span>
              <span><FaRegClock /> {post.readTime}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="svc-section post-body-section">
        <div className="svc-container post-body-container">
          <motion.article className="post-prose" {...getRevealProps('fadeUp', 0.05)}>
            {post.body.map(renderBlock)}
          </motion.article>
        </div>
      </section>

      <section className="svc-section svc-section-alt">
        <div className="svc-container">
          <header className="svc-section-head">
            <span className="svc-section-tag">Keep Reading</span>
            <h2 className="svc-section-title">More From the Team</h2>
          </header>
          <div className="svc-related-grid">
            {related.map((r) => (
              <Link to={`${basePath}/${r.slug}`} className="svc-related-card" key={r.slug}>
                <span>{r.title}</span>
                <FaArrowRight />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="svc-cta">
        <div className="svc-container">
          <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
            <h2 className="svc-cta-title">Have a question about this topic?</h2>
            <p className="svc-cta-desc">
              Talk to our team — we respond to every inquiry within one business day.
            </p>
            <div className="svc-cta-actions">
              <Link to="/contact" className="svc-btn svc-btn-primary">
                Get In Touch <FaArrowRight />
              </Link>
              <Link to={listPath} className="svc-btn svc-btn-ghost-light">
                Back to {listLabel}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticleDetailPage;
